import './index.css';

import Flashbar from '@cloudscape-design/components/flashbar';
import PropertyFilter from '@cloudscape-design/components/property-filter';
import Table from '@cloudscape-design/components/table';
import {
  and,
  or,
  orderBy,
  QueryFieldFilterConstraint,
  where,
} from 'firebase/firestore';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import useCustomInfiniteQuery from '../../hooks/use-customInfiniteQuery';

function Filter({
  tableId,
  setConstraints,
  filteringOptions,
  filteringProperties,
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const propertyFilters = useMemo(() => {
    const propertyKeys = searchParams.getAll(`${tableId}-key`);
    const operators = searchParams.getAll(`${tableId}-operator`);
    const values = searchParams.getAll(`${tableId}-value`);
    const operation = searchParams.get(`${tableId}-operation`) || 'and';
    const tokens = [];
    for (let i = 0; i < propertyKeys.length; i += 1) {
      tokens.push({
        propertyKey: propertyKeys[i],
        operator: operators[i],
        value: values[i],
      });
    }
    return {
      tokens,
      operation,
    };
  }, [searchParams, tableId]);

  const updateSearchParams = useCallback(({ detail }) => {
    const newSearchParams = {};
    searchParams.forEach((value, key) => {
      if (!key.startsWith(`${tableId}-`)) {
        newSearchParams[key] = value;
      }
    });

    const filteredTokens = detail.tokens.filter(
      (token) => (token.value && token.operator && token.propertyKey),
    );
    if (filteredTokens.length > 0) {
      newSearchParams[`${tableId}-key`] = filteredTokens.map((token) => token.propertyKey);
      newSearchParams[`${tableId}-operator`] = filteredTokens.map((token) => token.operator);
      newSearchParams[`${tableId}-value`] = filteredTokens.map((token) => token.value);
      newSearchParams[`${tableId}-operation`] = detail.operation;
    }
    setSearchParams(newSearchParams);
  }, [searchParams, setSearchParams, tableId]);

  useEffect(() => {
    if (propertyFilters.tokens.length === 0) {
      setConstraints([]);
      return;
    }
    const operator = {
      and,
      or,
    }[propertyFilters.operation];
    const filters = [];
    for (let i = 0; i < propertyFilters.tokens.length; i += 1) {
      const token = propertyFilters.tokens[i];
      // Special handling for material filter to make it case-insensitive
      // by using 'in' operator with both lower and upper case values
      if (token.propertyKey === 'material' && token.operator === '=') {
        filters.push(where(
          token.propertyKey,
          'in',
          [...new Set([token.value.toLowerCase(), token.value.toUpperCase()])],
        ));
      } else {
        filters.push(where(
          token.propertyKey,
          { '=': '==', '!=': '!=' }[token.operator],
          token.value,
        ));
      }
    }
    setConstraints([operator(...filters)]);
  }, [propertyFilters, setConstraints]);

  Filter.propTypes = {
    tableId: PropTypes.string.isRequired,
    setConstraints: PropTypes.func.isRequired,
    filteringOptions: PropTypes.arrayOf(PropTypes.shape({
      propertyKey: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })).isRequired,
    filteringProperties: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string.isRequired,
      operators: PropTypes.arrayOf(PropTypes.string).isRequired,
      propertyLabel: PropTypes.string.isRequired,
      groupValuesLabel: PropTypes.string.isRequired,
    })).isRequired,
  };

  return (
    <PropertyFilter
      onChange={updateSearchParams}
      query={propertyFilters}
      expandToViewport
      filteringOptions={filteringOptions}
      filteringProperties={filteringProperties}
    />
  );
}

function DataTable({
  id,
  header,
  collectionRef,
  filters, // required, pass empty array if no filters to avoid unnecessary re-renders
  orderField,
  pageSize,
  columnDefinitions,
  filteringOptions,
  filteringProperties,
  stickyColumns = null,
  resizableColumns = false,
  emptyMessage = 'No data',
  transformData = null,
  groupByKeys = [],
  trackBy = null,
}) {
  const [constraints, setConstraints] = useState([]);
  const [expandedItems, setExpandedItems] = useState([]);

  const queryParams = useMemo(
    () => [
      collectionRef,
      and(...filters, ...constraints),
      orderBy(orderField, 'desc'),
    ],
    [collectionRef, filters, constraints, orderField],
  );

  const {
    CustomPagination,
    pageData,
    error,
    isFetchingNextPage,
  } = useCustomInfiniteQuery({
    queryKey: `getDataTableDocs${id}`,
    queryParams,
    pageSize,
    transformData,
  });

  const items = useMemo(() => {
    if (pageData.length === 0) return [];
    if (groupByKeys.length === 0) return pageData;

    return Object.values(
      pageData.reduce((acc, item) => {
        const key = groupByKeys.map((k) => item[k]).join('-');

        if (!acc[key]) {
          return {
            ...acc,
            [key]: {
              ...item,
              children: [],
            },
          };
        }

        const { children, id: docId, ...rest } = acc[key];

        if (children.length === 0) {
          children.push({ id: docId, ...rest });
        }
        children.push(item);

        return {
          ...acc,
          [key]: {
            ...acc[key], id: `${docId}parent`, isParentRow: true, name: '', notes: '',
          },
        };
      }, {}),
    );
  }, [groupByKeys, pageData]);

  return (
    <Table
      variant="full-page"
      columnDefinitions={columnDefinitions}
      expandableRows={{
        getItemChildren: (item) => item.children ?? [],
        isItemExpandable: (item) => item.children?.length > 0,
        expandedItems,
        onExpandableItemToggle: ({ detail }) => setExpandedItems((prev) => {
          const next = new Set((prev ?? []).map((item) => item[trackBy]));
          if (detail.expanded) {
            next.add(detail.item[trackBy]);
          } else {
            next.delete(detail.item[trackBy]);
          }
          return [...next].map((itemId) => ({ [trackBy]: itemId }));
        }),
      }}
      trackBy={trackBy}
      items={items}
      loadingText="Loading..."
      loading={isFetchingNextPage}
      filter={(
        <Filter
          tableId={id}
          setConstraints={setConstraints}
          filteringOptions={filteringOptions}
          filteringProperties={filteringProperties}
        />
        )}
      empty={
          error ? (
            <Flashbar items={[{
              header: error?.message || 'Unknown error',
              type: 'error',
            }]}
            />
          ) : emptyMessage
        }
      header={header}
      pagination={<CustomPagination />}
      stickyColumns={stickyColumns}
      resizableColumns={resizableColumns}
    />
  );
}

DataTable.propTypes = {
  id: PropTypes.string.isRequired,
  header: PropTypes.node.isRequired,
  collectionRef: PropTypes.shape({}).isRequired,
  filters: PropTypes.arrayOf(PropTypes.instanceOf(QueryFieldFilterConstraint)).isRequired,
  pageSize: PropTypes.number.isRequired,
  orderField: PropTypes.string.isRequired,
  columnDefinitions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    header: PropTypes.string.isRequired,
    cell: PropTypes.func.isRequired,
  })).isRequired,
  filteringOptions: PropTypes.arrayOf(PropTypes.shape({
    propertyKey: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })).isRequired,
  filteringProperties: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    operators: PropTypes.arrayOf(PropTypes.string).isRequired,
    propertyLabel: PropTypes.string.isRequired,
    groupValuesLabel: PropTypes.string.isRequired,
  })).isRequired,
  stickyColumns: PropTypes.shape({
    first: PropTypes.number,
    last: PropTypes.number,
  }),
  groupByKeys: PropTypes.arrayOf(PropTypes.string),
  trackBy: PropTypes.string,
  resizableColumns: PropTypes.bool,
  emptyMessage: PropTypes.string,
  transformData: PropTypes.func,
};

export default DataTable;
